import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export const notificationMessageQueryKeys = {
  all: ['notification-messages'] as const,
  processing: (sessionId: string | null) =>
    [...notificationMessageQueryKeys.all, 'processing', sessionId] as const,
};

export type NotificationMessageStatus = 'processing' | 'success';

export interface NotificationMessage {
  id: string;
  user_id: string;
  account_id: string;
  type: string;
  status: NotificationMessageStatus;
  created: string;
  updated: string | null;
  title: string;
  description: string;
}

export interface NotificationMessageResponse {
  messages: NotificationMessage[];
  cursor?: string;
}

export interface PatchNotificationMessageItem {
  id: string;
  status: NotificationMessageStatus;
}

export async function getNotificationMessages(
  sessionId: string,
  scopeContext: ScopeContext,
  opts: { status?: NotificationMessageStatus; limit?: number; cursor?: string } = {},
): Promise<NotificationMessageResponse> {
  const qs = new URLSearchParams();
  if (opts.status) qs.set('status', opts.status);
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.cursor) qs.set('cursor', opts.cursor);
  const query = qs.toString();
  const path = query ? `/notification-message?${query}` : '/notification-message';

  const response = await backendClient.get(path, sessionId, scopeContext);
  if (response.success && response.data) {
    return response.data as NotificationMessageResponse;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch notification messages');
}

export async function patchNotificationMessages(
  sessionId: string,
  scopeContext: ScopeContext,
  messages: PatchNotificationMessageItem[],
): Promise<NotificationMessageResponse> {
  const response = await backendClient.patch('/notification-message', sessionId, { messages }, scopeContext);
  if (response.success && response.data) {
    return response.data as NotificationMessageResponse;
  }
  throw new BackendValidationError(response.error || 'Failed to update notification messages');
}
